import { useMatomo } from "@jonkoops/matomo-tracker-react";
import { Box, Button } from "@mui/material";
import { useState } from "react";
import {
  formatFileSize,
  lightenDarkenColor,
  useCSVReader,
} from "react-papaparse";

const GREY = "#CCC";
const GREY_LIGHT = "rgba(255, 255, 255, 0.4)";
const DEFAULT_REMOVE_HOVER_COLOR = "#A01919";
const REMOVE_HOVER_COLOR_LIGHT = lightenDarkenColor(
  DEFAULT_REMOVE_HOVER_COLOR,
  40,
);
const GREY_DIM = "#686868";

const LoadPatientRecordsFromCSV = ({
  infoText,
  setFileData,
  setFileLoadErrors,
}) => {
  const { trackEvent } = useMatomo();

  const { CSVReader } = useCSVReader();
  const [zoneHover, setZoneHover] = useState(false);
  const [removeHoverColor, setRemoveHoverColor] = useState(
    DEFAULT_REMOVE_HOVER_COLOR,
  );

  return (
    <CSVReader
      config={{
        skipEmptyLines: true,
      }}
      onUploadAccepted={({ data, errors, meta }) => {
        trackEvent({
          category: "Data Entry",
          action: "Upload patient data from file",
          name: "Upload patient data from file",
        });
        setZoneHover(false);
        setFileLoadErrors([]);
        setFileData(data);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        setZoneHover(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        setZoneHover(false);
      }}
      noDrag
    >
      {({
        getRootProps,
        acceptedFile,
        ProgressBar,
        getRemoveFileProps,
        Remove,
      }) => (
        <Box
          {...getRootProps()}
          sx={{
            alignItems: "center",
            border: `2px dashed ${zoneHover ? GREY_DIM : GREY}`,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            justifyContent: "center",
            p: 2.5,
            transition: "border-color 0.2s",
          }}
        >
          {acceptedFile ? (
            <Box
              sx={{
                background: "linear-gradient(to bottom, #EEE, #DDD)",
                borderRadius: 2,
                display: "flex",
                height: 120,
                width: 120,
                position: "relative",
                zIndex: 10,
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  px: 1.25,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: GREY_LIGHT,
                    borderRadius: 1,
                    mb: 0.5,
                    justifyContent: "center",
                    display: "flex",
                    px: 1,
                  }}
                >
                  {formatFileSize(acceptedFile.size)}
                </Box>
                <Box
                  sx={{
                    backgroundColor: GREY_LIGHT,
                    borderRadius: 1,
                    fontSize: 12,
                    mb: 0.5,
                    px: 1,
                  }}
                >
                  {acceptedFile.name}
                </Box>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  bottom: 14,
                  width: "100%",
                  px: 1.25,
                }}
              >
                <ProgressBar />
              </Box>
              <Box
                {...getRemoveFileProps()}
                sx={{
                  height: 23,
                  position: "absolute",
                  right: 6,
                  top: 6,
                  width: 23,
                  cursor: "pointer",
                }}
                onMouseOver={(event) => {
                  event.preventDefault();
                  setRemoveHoverColor(REMOVE_HOVER_COLOR_LIGHT);
                }}
                onMouseOut={(event) => {
                  event.preventDefault();
                  setRemoveHoverColor(DEFAULT_REMOVE_HOVER_COLOR);
                }}
              >
                <Remove color={removeHoverColor} />
              </Box>
            </Box>
          ) : (
            <Button
              color="primary"
              variant="contained"
              sx={{
                mt: 1,
              }}
            >
              {infoText}
            </Button>
          )}
        </Box>
      )}
    </CSVReader>
  );
};

export default LoadPatientRecordsFromCSV;
